'use client';

import { useCallback } from 'react';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import { useTrack } from '@frameio/shared/segment/hooks';
import { UPLOAD_LINK_SOURCE } from '@frameio/shared/upload-links/constants';
import {
  MenuButton,
  MenuGroup,
  MenuLink,
  Spinner,
  Text,
  useToast,
} from '@frameio/vapor';
import { IconArrowUpRight, IconCopy, IconLink } from '@frameio/vapor-icons';
import { useGetFolderUploadLinksQuery } from '../gql/queries/generated/GetFolderUploadLinks';
import { useCopyUploadLink } from '../hooks/useCopyUploadLink';
import styles from './CopyUploadLinkMenuItem.module.css';
import TEXT from './generated/TEXT/CopyUploadLinkMenuItem';

TEXT.DEFINE({
  /** Label for the menu item that copies a folder's upload link */
  copyUploadLink: 'Copy Upload Link',
  /** Secondary label showing who created an upload link */
  createdBy: (name: string) => `Created by ${name}`,
  /** Success toast shown after an upload link is copied to the clipboard */
  uploadLinkCopied: 'Upload link copied',
  /** Label for the submenu containing upload links */
  subNavLabel: 'Active Upload Links',
  /** Menu entry that navigates to the project's Upload Links settings page */
  allUploadLinks: 'All Upload Links',
});

/** Upper bound on how many upload links are listed in the submenu. */
const MAX_UPLOAD_LINKS = 50;
const VISIBLE_UPLOAD_LINK_ROWS = 8;
const TOAST_DURATION_MS = 2000;

type CopyUploadLinkMenuItemProps = {
  folderId: string;
};

/**
 * Lists a folder's active upload links inside the "Copy Upload Link" submenu.
 *
 * The links are fetched when this component mounts. Because it only renders
 * inside the submenu's popover (which mounts on open), the fetch happens when
 * the submenu opens rather than when the parent context menu opens — keeping
 * the context menu cheap to open.
 */
function CopyUploadLinkSubmenu({ folderId }: CopyUploadLinkMenuItemProps) {
  const copyUploadLink = useCopyUploadLink();
  const { addToast } = useToast();
  const track = useTrack();
  const projectId = useActiveProjectId();

  const { data, loading } = useGetFolderUploadLinksQuery({
    variables: { folderId, page: { first: MAX_UPLOAD_LINKS } },
  });

  const asset = data?.account?.asset;
  const links =
    asset?.__typename === 'FolderAsset' ? asset.uploadLinks?.nodes ?? [] : [];

  const handleCopy = useCallback(
    async (url: string, uploadLinkId: string) => {
      await copyUploadLink(url);
      track('upload-link-copied', {
        upload_link_id: uploadLinkId,
        project_id: projectId,
        source: UPLOAD_LINK_SOURCE.FinderSubmenu,
      });
      addToast(
        { title: TEXT.uploadLinkCopied, variant: 'success' },
        { duration: TOAST_DURATION_MS }
      );
    },
    [copyUploadLink, track, projectId, addToast]
  );

  return (
    <>
      <MenuGroup label={TEXT.subNavLabel}>
        {loading ? (
          <div className={styles.loadingRow}>
            <Spinner
              aria-label={TEXT.subNavLabel}
              delay={0}
              isLoading={true}
              size="small"
            />
          </div>
        ) : (
          <div
            className={styles.uploadLinksContainer}
            data-visible-row-count={VISIBLE_UPLOAD_LINK_ROWS}
          >
            {links.map((link) => (
              <MenuButton
                key={link.id}
                details={
                  link.creator?.name
                    ? TEXT.createdBy(link.creator.name)
                    : undefined
                }
                disabled={!link.url}
                iconBefore={<IconCopy />}
                onPress={() => link.url && handleCopy(link.url, link.id)}
                shouldCloseMenuOnSelect={true}
              >
                <Text truncate={true} variant="body">
                  {link.name}
                </Text>
              </MenuButton>
            ))}
          </div>
        )}
      </MenuGroup>
      <MenuGroup>
        <MenuLink
          href={getLink({ page: 'uploadLinks', projectId: projectId ?? '' })}
          iconAfter={<IconArrowUpRight />}
        >
          {TEXT.allUploadLinks}
        </MenuLink>
      </MenuGroup>
    </>
  );
}

/**
 * "Copy Upload Link" context-menu entry for a folder. It only renders when the
 * folder is known to have active upload links (gated by useCopyUploadLinkConfig
 * via the hydrated hasActiveUploadLinks flag), and presents those links in a
 * submenu whose contents are fetched lazily when the submenu opens.
 */
export function CopyUploadLinkMenuItem({
  folderId,
}: CopyUploadLinkMenuItemProps) {
  return (
    <MenuButton
      iconBefore={<IconLink />}
      submenu={<CopyUploadLinkSubmenu folderId={folderId} />}
    >
      {TEXT.copyUploadLink}
    </MenuButton>
  );
}
